import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { LiveDrawResults } from "../components/LiveDrawResults";
import { TvBoard } from "../components/TvBoard";
import { MessageCircle, Smartphone } from "lucide-react";
import { DEFAULT_PUBLIC_SETTINGS, themeStyle, bgStyle } from "../utils/brandTheme";

export const ContactBar = ({ s }) => (
  <section className="rounded-2xl bg-slate-900 text-white" data-testid="public-results-contact">
    <div className="px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <p className="text-xl md:text-2xl font-black tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }} data-testid="public-brand">
        {s.brand_name}
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        {s.whatsapp && (
          <a href={`https://wa.me/506${s.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="public-whatsapp"
            className="flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 px-5 h-11 font-semibold transition-colors">
            <MessageCircle className="w-5 h-5" /> WhatsApp {s.whatsapp}
          </a>
        )}
        {s.sinpe && (
          <div className="flex items-center justify-center gap-2 rounded-full bg-white/10 px-5 h-11 font-semibold" data-testid="public-sinpe">
            <Smartphone className="w-5 h-5 text-brand" /> Sinpe {s.sinpe}
          </div>
        )}
      </div>
    </div>
  </section>
);

export default function PublicResults() {
  const { token } = useParams();
  const tv = new URLSearchParams(useLocation().search).get("tv") === "1";
  const [invalid, setInvalid] = useState(false);
  const [s, setS] = useState(DEFAULT_PUBLIC_SETTINGS);
  const fetcher = useCallback(async (day) => {
    try {
      const r = await axios.get(`${API}/public/lottery-results/${token}?day=${day}`);
      if (r.data.settings) setS(r.data.settings);
      return r;
    } catch (e) {
      if (e.response?.status === 404) setInvalid(true);
      throw e;
    }
  }, [token]);
  const promoFetcher = useCallback(() => axios.get(`${API}/public/tv-promos/${token}`), [token]);

  useEffect(() => { document.title = `Resultados de Sorteos | ${s.brand_name}`; }, [s.brand_name]);

  if (tv && !invalid) return <TvBoard fetcher={fetcher} promoFetcher={promoFetcher} settings={s} />;

  const hasBg = !!s.bg_image_url;
  return (
    <div className="min-h-screen flex flex-col bg-slate-50" style={{ ...themeStyle(s), ...bgStyle(s) }} data-testid="public-results-page">
      <header className="bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <span className="text-white text-lg font-black tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{s.brand_name}</span>
          <span className="text-xs text-brand font-semibold uppercase tracking-wider">Resultados en vivo</span>
        </div>
      </header>
      <main className={`flex-1 max-w-6xl w-full mx-auto px-3 sm:px-4 py-6 ${hasBg ? "my-4 sm:my-6 rounded-3xl bg-white/85 backdrop-blur-md shadow-xl" : ""}`}>
        {invalid ? (
          <div className="text-center py-20" data-testid="public-results-invalid">
            <p className="text-xl font-bold text-slate-800">Este link ya no está disponible</p>
            <p className="text-sm text-slate-500 mt-2">Pide el link actualizado por WhatsApp al {s.whatsapp}.</p>
          </div>
        ) : (
          <LiveDrawResults fetcher={fetcher} publicMode headerExtra={<ContactBar s={s} />} />
        )}
      </main>
    </div>
  );
}
